import React, { useState, useEffect, useRef, useCallback } from "react";

/**
 * HandbookJeopardy: Student Handbook basics review game for King's Academy.
 * Built on the MotionJeopardy model. Self-contained: no external dependencies,
 * all styles namespaced under `.jpd`.
 * Physical buzzers in the room; the teacher judges each answer with the
 * Correct / Wrong buttons. Wrong answers dock the value, lock the team out,
 * and leave the clue open for a steal. Two random Daily Doubles double the stakes.
 *
 * Every clue is drawn from the US Student and Parent Handbook. Each clue carries
 * a `src` field naming the handbook section, shown under the answer on reveal.
 * To update for a new handbook edition, edit the CATEGORIES array only.
 *
 * Drop-in: default export, no required props. Renders anywhere in the portal.
 */

const CATEGORIES = [
  {
    name: "Fundamental Rules",
    clues: [
      { v: 100, q: "Fundamental School Rule violations are serious enough that they are NOT handled through this points system.", a: "What are MOPs (Missed Obligation Points)?", src: "Fundamental School Rules" },
      { v: 200, q: "Tampering with this kind of equipment, such as extinguishers and smoke detectors, breaks a Fundamental School Rule.", a: "What is fire safety equipment?", src: "Fundamental School Rules" },
      { v: 300, q: "Alcohol, tobacco, illegal substances, and these battery-powered devices are all banned for use or possession.", a: "What are e-cigarettes (vapes)?", src: "Fundamental School Rules" },
      { v: 400, q: "Being found in either of these two places belonging to a member of the opposite sex is a Fundamental School Rule violation.", a: "What is their house or their room?", src: "Fundamental School Rules" },
      { v: 500, q: "The weapons rule covers more than the real thing. It also bans possessing these look-alikes.", a: "What are weapon facsimiles (replicas)?", src: "Fundamental School Rules" },
    ],
  },
  {
    name: "MOP Math",
    clues: [
      { v: 100, q: "The three words that MOPs stands for.", a: "What are Missed Obligation Points?", src: "Conduct System and Student Accountability" },
      { v: 200, q: "Missing a class costs you this many MOPs.", a: "What is 5 MOPs?", src: "Conduct System: list of MOPs" },
      { v: 300, q: "Missing a co-curricular, a School Meeting, or a sit-down lunch each costs this many MOPs.", a: "What is 3 MOPs?", src: "Conduct System: list of MOPs" },
      { v: 400, q: "A dress code violation or a class tardy costs a number of MOPs in this range.", a: "What is 1 to 2 MOPs?", src: "Conduct System: list of MOPs" },
      { v: 500, q: "In one week you skip a class, skip School Meeting, and miss an individual required meeting. Your MOP total.", a: "What is 10 MOPs (5 + 3 + 2)?", src: "Conduct System: list of MOPs" },
    ],
  },
  {
    name: "Crossing the Line",
    clues: [
      { v: 100, q: "MOPs go back to zero at the start of each of these. There are three a year.", a: "What is a term (fall, winter, spring)?", src: "Conduct System and Student Accountability" },
      { v: 200, q: "Reach 15 MOPs in a term and you are placed on this status, with a community work detail.", a: "What is Disciplinary Warning?", src: "Conduct System: Consequences" },
      { v: 300, q: "This many MOPs in a term means Disciplinary Restriction and a Weekend Detention.", a: "What is 30 MOPs?", src: "Conduct System: Consequences" },
      { v: 400, q: "At 45 MOPs in a term you land on this status, and your case goes to the head of the Upper School and the dean of students.", a: "What is Disciplinary Report?", src: "Conduct System: Consequences" },
      { v: 500, q: "Weekend Detention keeps you on campus from Thursday after your commitments until this day and time.", a: "What is Saturday at noon?", src: "Conduct System: Weekend Detention" },
    ],
  },
  {
    name: "Phone Home",
    clues: [
      { v: 100, q: "Phone use is not permitted during the academic day: Sunday through Thursday, from breakfast until this time.", a: "What is 16:00?", src: "Mobile Phones: specifics of the policy" },
      { v: 200, q: "First offense: your phone is confiscated and you can collect it at the end of this.", a: "What is the school day?", src: "Mobile Phones: disciplinary response" },
      { v: 300, q: "Second offense: your phone is confiscated and you can collect it at the end of this.", a: "What is the school week?", src: "Mobile Phones: disciplinary response" },
      { v: 400, q: "Third offense: these people are asked to come in to school, or join a Zoom call if you are a boarder.", a: "Who are your parents?", src: "Mobile Phones: disciplinary response" },
      { v: 500, q: "Fifth offense: you are asked to appear in front of this committee.", a: "What is the Community Standards Committee (CSC)?", src: "Mobile Phones: disciplinary response" },
    ],
  },
  {
    name: "Dress the Part",
    clues: [
      { v: 100, q: "Class dress requires this King's Academy item, appropriately knotted.", a: "What is the King's Academy tie?", src: "Dress Code: class dress" },
      { v: 200, q: "The color of school-approved pants and skirts.", a: "What is tan?", src: "Dress Code: class dress" },
      { v: 300, q: "On Thursdays, this may replace the long-sleeved shirt and tie.", a: "What is a King's Academy polo shirt?", src: "Dress Code: class dress" },
      { v: 400, q: "Formal dress is class dress plus this item. A King's knit sweater may stand in for it.", a: "What is the King's Academy blazer?", src: "Dress Code: formal dress" },
      { v: 500, q: "Uggs, Birkenstocks, and other slip-on shoes are not permitted for this specific reason.", a: "What is safety (risk of tripping and falling in an emergency)?", src: "Dress Code: class dress" },
    ],
  },
  {
    name: "Around Campus",
    clues: [
      { v: 100, q: "Lunch is a required sit-down meal every weekday except this one, when it is a buffet.", a: "What is Thursday?", src: "The Hess Family Dining Hall: sit-down meals" },
      { v: 200, q: "Feeling unwell during the class day? This is the only place that can excuse an absence due to illness.", a: "What is the Health Center?", src: "Academic Life: Attendance" },
      { v: 300, q: "On school nights in the Houses, this is the time for lights out.", a: "What is 22:15?", src: "Residential Life: house schedule" },
      { v: 400, q: "Boarders leaving for the weekend must get their permission form to the OSL by this day and time.", a: "What is Tuesday at 14:00?", src: "Residential Life: weekend permissions" },
      { v: 500, q: "Miss this percentage or more of a course's class meetings in a semester, excused or not, and you may not receive credit.", a: "What is 20%?", src: "Appendix III: Attendance Policy" },
    ],
  },
];

const CSS = `
.jpd{
  --navy-deep:#060f2e; --navy:#0b1e50; --navy-tile:#0d2360; --navy-tile-hover:#123086;
  --gold:#f2c14e; --gold-bright:#ffd766; --cream:#f7f3e8; --cream-dim:#d9d3c2;
  --used:#0a1636; --terracotta:#cf6a4c; --terracotta-bright:#e07d5e;
  --green:#4c9a6a; --green-bright:#5fb37f;
  --heading:"Cambria","Georgia","Times New Roman",serif;
  --body:"Calibri","Segoe UI",system-ui,-apple-system,sans-serif;
  font-family:var(--body); color:var(--cream); background:var(--navy-deep);
  min-height:100%; width:100%; position:relative;
}
.jpd *{box-sizing:border-box;margin:0;padding:0;}
.jpd button{font-family:var(--body);cursor:pointer;border:none;}
.jpd button:focus-visible{outline:3px solid var(--gold-bright);outline-offset:2px;}

.jpd .setup{min-height:100vh;display:flex;flex-direction:column;align-items:center;
  justify-content:center;text-align:center;padding:24px;gap:26px;}
.jpd .logo{font-family:var(--heading);color:var(--gold);text-shadow:4px 4px 0 rgba(0,0,0,.45);line-height:.95;}
.jpd .logo .big{display:block;font-size:clamp(3rem,11vw,7rem);font-weight:700;letter-spacing:.02em;}
.jpd .logo .sub{display:block;font-size:clamp(1rem,3.2vw,1.7rem);color:var(--cream);margin-top:.4em;letter-spacing:.3em;}
.jpd .rule{width:min(520px,80vw);height:4px;background:var(--gold);border-radius:2px;}
.jpd .panel{background:var(--navy);border:2px solid var(--gold);border-radius:14px;padding:26px 28px;
  width:min(560px,92vw);display:flex;flex-direction:column;gap:18px;}
.jpd .panel h2{font-family:var(--heading);color:var(--gold-bright);font-size:1.4rem;font-weight:700;}
.jpd .count{display:flex;gap:10px;justify-content:center;}
.jpd .count button{background:var(--navy-tile);color:var(--cream);border:2px solid transparent;
  font-size:1.4rem;font-weight:700;width:56px;height:56px;border-radius:10px;transition:background .12s,border-color .12s;}
.jpd .count button:hover{background:var(--navy-tile-hover);}
.jpd .count button.active{border-color:var(--gold);color:var(--gold-bright);background:var(--navy-tile-hover);}
.jpd .names{display:flex;flex-direction:column;gap:10px;}
.jpd .names input{font-size:1.1rem;padding:11px 14px;border-radius:8px;border:2px solid var(--navy-tile-hover);
  background:var(--navy-deep);color:var(--cream);font-family:var(--body);}
.jpd .names input:focus{outline:none;border-color:var(--gold);}
.jpd .start{background:var(--gold);color:var(--navy-deep);font-size:1.4rem;font-weight:700;padding:15px;
  border-radius:10px;font-family:var(--heading);transition:background .12s,transform .08s;}
.jpd .start:hover{background:var(--gold-bright);}
.jpd .start:active{transform:scale(.98);}
.jpd .hint{color:var(--cream-dim);font-size:.92rem;line-height:1.5;max-width:560px;}
.jpd .hint strong{color:var(--cream);}

.jpd .game{min-height:100vh;display:flex;flex-direction:column;}
.jpd .topbar{display:flex;align-items:center;justify-content:space-between;padding:10px 18px;
  background:var(--navy);border-bottom:2px solid var(--gold);gap:12px;}
.jpd .topbar h1{font-family:var(--heading);color:var(--gold);font-size:1.25rem;font-weight:700;}
.jpd .tools{display:flex;gap:8px;}
.jpd .tool{background:var(--navy-tile);color:var(--cream);font-size:.9rem;font-weight:600;padding:9px 14px;
  border-radius:8px;transition:background .12s;}
.jpd .tool:hover{background:var(--navy-tile-hover);}

.jpd .board{flex:1;display:grid;grid-template-columns:repeat(6,1fr);gap:8px;padding:12px;background:var(--navy-deep);}
.jpd .cat{background:var(--navy);color:var(--gold-bright);font-family:var(--heading);font-weight:700;
  display:flex;align-items:center;justify-content:center;text-align:center;padding:10px 6px;border-radius:8px;
  font-size:clamp(.8rem,1.35vw,1.2rem);line-height:1.15;text-transform:uppercase;letter-spacing:.01em;min-height:60px;}
.jpd .tile{background:var(--navy-tile);color:var(--gold);font-family:var(--heading);font-weight:700;
  font-size:clamp(1.4rem,3.2vw,2.5rem);display:flex;align-items:center;justify-content:center;border-radius:8px;
  transition:background .12s,transform .08s;text-shadow:2px 2px 0 rgba(0,0,0,.4);min-height:56px;}
.jpd .tile:hover{background:var(--navy-tile-hover);}
.jpd .tile:active{transform:scale(.97);}
.jpd .tile.used{background:var(--used);color:transparent;text-shadow:none;cursor:default;pointer-events:none;}

.jpd .scorebar{display:flex;gap:10px;padding:12px 14px;background:var(--navy);border-top:2px solid var(--gold);
  flex-wrap:wrap;justify-content:center;}
.jpd .team{background:var(--navy-tile);border-radius:12px;padding:10px 14px;min-width:150px;text-align:center;}
.jpd .team .name{font-weight:700;color:var(--cream);font-size:1rem;margin-bottom:2px;white-space:nowrap;
  overflow:hidden;text-overflow:ellipsis;max-width:180px;}
.jpd .team .score{font-family:var(--heading);color:var(--gold-bright);font-size:2rem;font-weight:700;line-height:1;}
.jpd .team .adj{display:flex;gap:6px;justify-content:center;margin-top:8px;}
.jpd .team .adj button{background:var(--navy-deep);color:var(--cream);font-size:1rem;font-weight:700;width:34px;
  height:30px;border-radius:6px;transition:background .12s;}
.jpd .team .adj button:hover{background:var(--navy-tile-hover);}

.jpd .overlay{position:fixed;inset:0;background:var(--navy);display:flex;flex-direction:column;align-items:center;
  justify-content:center;padding:5vh 6vw;z-index:20;text-align:center;overflow-y:auto;}
.jpd .clue-meta{color:var(--gold);font-size:1.3rem;font-weight:700;letter-spacing:.05em;margin-bottom:4vh;}
.jpd .clue-text{font-family:var(--heading);color:var(--cream);font-weight:700;
  font-size:clamp(1.6rem,4.4vw,3.6rem);line-height:1.25;max-width:24ch;animation:jpdPop .18s ease-out;}
.jpd .answer-text{font-family:var(--heading);color:var(--gold-bright);font-weight:700;
  font-size:clamp(1.5rem,4vw,3rem);line-height:1.3;margin-top:4vh;max-width:26ch;}
.jpd .answer-src{color:var(--cream-dim);font-size:1rem;margin-top:1.5vh;letter-spacing:.02em;}
.jpd .clue-status{color:var(--terracotta-bright);font-size:1.05rem;font-weight:600;margin-top:3vh;
  min-height:1.4em;line-height:1.4;max-width:40ch;}
.jpd .judge-row{margin-top:3.5vh;display:flex;gap:14px;flex-wrap:wrap;justify-content:center;}
.jpd .tj{background:var(--navy-tile);border-radius:12px;padding:10px 12px 12px;display:flex;flex-direction:column;
  gap:8px;min-width:150px;border:2px solid transparent;transition:opacity .12s;}
.jpd .tj .tj-name{font-weight:700;color:var(--cream);font-size:1.05rem;white-space:nowrap;overflow:hidden;
  text-overflow:ellipsis;max-width:170px;text-align:center;}
.jpd .tj .tj-btns{display:flex;gap:8px;}
.jpd .tj .tj-btns button{flex:1;font-size:1.05rem;font-weight:700;padding:12px 10px;border-radius:8px;
  transition:background .12s,transform .08s;}
.jpd .tj .tj-btns button:active{transform:scale(.96);}
.jpd .tj .correct{background:var(--green);color:#fff;}
.jpd .tj .correct:hover{background:var(--green-bright);}
.jpd .tj .wrong{background:var(--terracotta);color:#fff;}
.jpd .tj .wrong:hover{background:var(--terracotta-bright);}
.jpd .tj.out{opacity:.4;}
.jpd .tj.out .tj-name::after{content:" (out)";color:var(--terracotta-bright);font-weight:600;}
.jpd .tj.locked .tj-btns button{pointer-events:none;opacity:.5;}
.jpd .tj.out .tj-btns button{pointer-events:none;}

.jpd .clue-actions{margin-top:4vh;display:flex;gap:12px;flex-wrap:wrap;justify-content:center;}
.jpd .clue-actions button{font-size:1.15rem;font-weight:700;padding:14px 22px;border-radius:10px;
  transition:background .12s,transform .08s;}
.jpd .clue-actions button:active{transform:scale(.97);}
.jpd .reveal{background:var(--gold);color:var(--navy-deep);font-family:var(--heading);}
.jpd .reveal:hover{background:var(--gold-bright);}
.jpd .close{background:var(--navy-tile);color:var(--cream-dim);border:2px solid var(--cream-dim);}
.jpd .close:hover{background:var(--navy-tile-hover);color:var(--cream);}

.jpd .dd{position:fixed;inset:0;background:var(--navy-deep);z-index:30;display:flex;align-items:center;
  justify-content:center;flex-direction:column;cursor:pointer;}
.jpd .dd .dd-word{font-family:var(--heading);color:var(--gold);font-weight:700;
  font-size:clamp(2.5rem,10vw,7rem);text-shadow:5px 5px 0 rgba(0,0,0,.5);letter-spacing:.03em;text-align:center;
  line-height:1;animation:jpdPop .18s ease-out;}
.jpd .dd .dd-note{color:var(--cream-dim);margin-top:3vh;font-size:1.1rem;}

@keyframes jpdPop{from{transform:scale(.85);opacity:0;}to{transform:scale(1);opacity:1;}}
@media(prefers-reduced-motion:reduce){.jpd *{transition:none!important;animation:none!important;}}
@media(max-width:640px){.jpd .board{gap:5px;padding:6px;}.jpd .team{min-width:120px;}}
`;

const keyOf = (c, r) => `${c}-${r}`;

function HandbookJeopardy() {
  const [phase, setPhase] = useState("setup");
  const [numTeams, setNumTeams] = useState(3);
  const [names, setNames] = useState(["Team 1", "Team 2", "Team 3", "Team 4"]);
  const [scores, setScores] = useState([0, 0, 0, 0]);
  const [dd, setDd] = useState([]); // keys of the two daily doubles
  const [used, setUsed] = useState([]); // keys used
  const [active, setActive] = useState(null); // {col,row,isDD,value}
  const [settled, setSettled] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [out, setOut] = useState([]); // team indices locked out this clue
  const [gotBy, setGotBy] = useState(null);
  const [showDD, setShowDD] = useState(false);
  const rootRef = useRef(null);

  const activeNames = names.slice(0, numTeams);

  const clueValue = active ? (active.isDD ? active.value * 2 : active.value) : 0;

  const startGame = () => {
    setScores(Array(numTeams).fill(0));
    const picks = new Set();
    while (picks.size < 2) {
      picks.add(keyOf(Math.floor(Math.random() * 6), Math.floor(Math.random() * 5)));
    }
    setDd([...picks]);
    setUsed([]);
    setPhase("game");
  };

  const openClue = (c, r) => {
    const k = keyOf(c, r);
    if (used.includes(k)) return;
    const isDD = dd.includes(k);
    setActive({ col: c, row: r, isDD, value: CATEGORIES[c].clues[r].v });
    setSettled(false);
    setRevealed(false);
    setOut([]);
    setGotBy(null);
    if (isDD) setShowDD(true);
  };

  const markCorrect = (i) => {
    if (settled || revealed || out.includes(i)) return;
    setScores((s) => s.map((v, idx) => (idx === i ? v + clueValue : v)));
    setGotBy(i);
    setSettled(true);
    setRevealed(true);
  };

  const markWrong = (i) => {
    if (settled || revealed || out.includes(i)) return;
    setScores((s) => s.map((v, idx) => (idx === i ? v - clueValue : v)));
    setOut((o) => [...o, i]);
  };

  const revealAnswer = useCallback(() => {
    if (settled) return;
    setRevealed(true);
  }, [settled]);

  const closeClue = useCallback(() => {
    if (!active) return;
    setUsed((u) => [...u, keyOf(active.col, active.row)]);
    setActive(null);
  }, [active]);

  const dismissDD = () => setShowDD(false);

  const adjust = (i, delta) =>
    setScores((s) => s.map((v, idx) => (idx === i ? v + delta : v)));

  const toggleFullscreen = () => {
    const el = rootRef.current;
    if (!document.fullscreenElement) el?.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const resetGame = () => {
    if (window.confirm("Start a brand new game? Current scores will be cleared.")) {
      setActive(null);
      setPhase("setup");
    }
  };

  // keyboard: Esc closes / dismisses DD; Space reveals
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (showDD) { setShowDD(false); return; }
        if (active) closeClue();
      }
      if (e.key === " " && active && !showDD && !settled && !revealed) {
        e.preventDefault();
        revealAnswer();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, showDD, settled, revealed, closeClue, revealAnswer]);

  const status = () => {
    if (gotBy !== null)
      return { color: "var(--green-bright)", text: `\u2713 ${activeNames[gotBy]} got it!  +$${clueValue}` };
    if (out.length >= numTeams)
      return { color: "var(--terracotta-bright)", text: "Everyone is out. Reveal the answer to move on." };
    if (out.length > 0) {
      const ns = out.map((i) => activeNames[i]);
      return {
        color: "var(--terracotta-bright)",
        text: `\u2717 ${ns.join(", ")} ${ns.length > 1 ? "are" : "is"} out. Still open: anyone else can steal it!`,
      };
    }
    if (revealed)
      return { color: "var(--cream-dim)", text: "Answer revealed. No points awarded." };
    return { color: "", text: "" };
  };

  return (
    <div className="jpd" ref={rootRef}>
      <style>{CSS}</style>

      {phase === "setup" && (
        <section className="setup">
          <div className="logo">
            <span className="big">JEOPARDY!</span>
            <span className="sub">STUDENT HANDBOOK &middot; THE BASICS</span>
          </div>
          <div className="rule" />
          <div className="panel">
            <h2>How many teams?</h2>
            <div className="count">
              {[2, 3, 4].map((n) => (
                <button
                  key={n}
                  className={numTeams === n ? "active" : ""}
                  onClick={() => setNumTeams(n)}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="names">
              {Array.from({ length: numTeams }).map((_, i) => (
                <input
                  key={i}
                  type="text"
                  value={names[i]}
                  placeholder={`Team ${i + 1} name`}
                  onChange={(e) =>
                    setNames((arr) => arr.map((v, idx) => (idx === i ? e.target.value : v)))
                  }
                />
              ))}
            </div>
            <button className="start" onClick={startGame}>
              Start the game
            </button>
          </div>
          <p className="hint">
            Click a dollar value to show a clue. Teams buzz in with their bells or clickers, and you judge who
            was first. Tap <strong>&#10003; Correct</strong> to award the value, or <strong>&#10007; Wrong</strong> to
            dock it and lock that team out. The clue stays open for anyone else to steal. Reveal the answer when it's
            settled; the handbook section appears under it. Two hidden <strong>Daily Doubles</strong> put double
            points at stake, win or lose. The &minus;/+ buttons under each team fix scores by hand.
          </p>
        </section>
      )}

      {phase === "game" && (
        <section className="game">
          <div className="topbar">
            <h1>Student Handbook Review</h1>
            <div className="tools">
              <button className="tool" onClick={toggleFullscreen}>Fullscreen</button>
              <button className="tool" onClick={resetGame}>New game</button>
            </div>
          </div>

          <div className="board">
            {CATEGORIES.map((cat, c) => (
              <div className="cat" key={`h-${c}`}>{cat.name}</div>
            ))}
            {Array.from({ length: 5 }).map((_, r) =>
              CATEGORIES.map((cat, c) => {
                const k = keyOf(c, r);
                const isUsed = used.includes(k);
                return (
                  <button
                    key={k}
                    className={`tile${isUsed ? " used" : ""}`}
                    onClick={() => openClue(c, r)}
                    disabled={isUsed}
                  >
                    ${cat.clues[r].v}
                  </button>
                );
              })
            )}
          </div>

          <div className="scorebar">
            {activeNames.map((name, i) => (
              <div className="team" key={i}>
                <div className="name">{name}</div>
                <div className="score">{scores[i]}</div>
                <div className="adj">
                  <button onClick={() => adjust(i, -100)}>&minus;</button>
                  <button onClick={() => adjust(i, 100)}>+</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {active && !showDD && (
        <div className="overlay">
          <div className="clue-meta">
            {CATEGORIES[active.col].name} &middot; ${active.value}
            {active.isDD ? `  (Daily Double: $${clueValue} at stake!)` : ""}
          </div>
          <div className="clue-text">{CATEGORIES[active.col].clues[active.row].q}</div>
          {revealed && (
            <>
              <div className="answer-text">{CATEGORIES[active.col].clues[active.row].a}</div>
              <div className="answer-src">Handbook: {CATEGORIES[active.col].clues[active.row].src}</div>
            </>
          )}
          {(() => {
            const s = status();
            return <div className="clue-status" style={{ color: s.color }}>{s.text}</div>;
          })()}

          <div className="judge-row">
            {activeNames.map((name, i) => {
              const isOut = out.includes(i);
              const locked = settled || revealed;
              return (
                <div
                  className={`tj${isOut ? " out" : ""}${locked && !isOut ? " locked" : ""}`}
                  key={i}
                >
                  <div className="tj-name">{name}</div>
                  <div className="tj-btns">
                    <button className="correct" onClick={() => markCorrect(i)}>
                      &#10003; +${clueValue}
                    </button>
                    <button className="wrong" onClick={() => markWrong(i)}>
                      &#10007; &minus;${clueValue}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="clue-actions">
            {!settled && !revealed ? (
              <>
                <button className="reveal" onClick={revealAnswer}>Reveal answer</button>
                <button className="close" onClick={closeClue}>Skip and close</button>
              </>
            ) : (
              <button className="reveal" onClick={closeClue}>Next clue &rarr;</button>
            )}
          </div>
        </div>
      )}

      {showDD && (
        <div className="dd" onClick={dismissDD}>
          <div className="dd-word">DAILY<br />DOUBLE!</div>
          <div className="dd-note">Click anywhere to see the clue. Worth double points.</div>
        </div>
      )}
    </div>
  );
}

export { HandbookJeopardy };
export default HandbookJeopardy;